import type { FinancialSnapshot } from "../fna/content/fnaBasicConfig.js";
import { computeSummaries, formatMoney } from "../fna/content/fnaBasicUtils.js";

export type ZuuFinancialRow = {
  code: string;
  label: string;
  value: string;
  note?: string;
};

export type ZuuFinancialGroup = {
  key: string;
  title: string;
  description: string;
  summaryLabel?: string;
  summaryValue: string;
  rows: ZuuFinancialRow[];
};

const EMPTY_VALUE = "--";
const EMPTY_OPTION = "無";

function normalizeTextLikeValue(value: unknown): string | null {
  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized || null;
  }

  if (typeof value === "number") {
    return Number.isNaN(value) ? null : String(value);
  }

  if (typeof value === "boolean") {
    return value ? "是" : "否";
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => normalizeTextLikeValue(item))
      .filter((item): item is string => Boolean(item));

    return normalized.length > 0 ? normalized.join("、") : null;
  }

  return null;
}

function formatCurrencyValue(amount?: number) {
  return amount === undefined || amount === null || Number.isNaN(amount)
    ? EMPTY_VALUE
    : `${formatMoney(amount)} HKD`;
}

function formatTextValue(value?: unknown) {
  return normalizeTextLikeValue(value) ?? EMPTY_VALUE;
}

function formatNamedCurrency(
  enabled: boolean | undefined,
  name: unknown,
  amount: number | undefined
) {
  if (!enabled) return EMPTY_OPTION;

  const normalizedName = normalizeTextLikeValue(name);
  const amountValue = formatCurrencyValue(amount);

  if (normalizedName && amountValue !== EMPTY_VALUE) return `${normalizedName} · ${amountValue}`;
  if (normalizedName) return normalizedName;
  return amountValue;
}

export function buildZuuFinancialGroups(snapshot: FinancialSnapshot | undefined): ZuuFinancialGroup[] {
  const summaries = snapshot ? computeSummaries(snapshot) : undefined;

  return [
    {
      key: "income",
      title: "1. 收入資料",
      description: "按 ZUU 查看頁節奏整理過去 24 個月平均每月收入。",
      summaryLabel: "每月收入小計",
      summaryValue: formatCurrencyValue(summaries?.totalIncome),
      rows: [
        { code: "1a", label: "薪金 / 佣金 / 花紅", value: formatCurrencyValue(snapshot?.income?.salaryCommissionBonus) },
        { code: "1b", label: "租金收入", value: formatCurrencyValue(snapshot?.income?.rentalIncome) },
        {
          code: "1c",
          label: "其他收入",
          value: formatNamedCurrency(
            snapshot?.income?.hasOtherIncome,
            snapshot?.income?.otherIncomeName,
            snapshot?.income?.otherIncomeAmount,
          ),
        },
      ],
    },
    {
      key: "expense",
      title: "2. 支出資料",
      description: "以查看態列出固定支出與其他經常性開支。",
      summaryLabel: "每月支出小計",
      summaryValue: formatCurrencyValue(summaries?.totalExpense),
      rows: [
        { code: "1d", label: "個人及家庭開支", value: formatCurrencyValue(snapshot?.expense?.personalAndFamilyExpense) },
        { code: "1e", label: "按揭 / 借貸還款", value: formatCurrencyValue(snapshot?.expense?.mortgageLoanRepayment) },
        { code: "1f", label: "保險供款", value: formatCurrencyValue(snapshot?.expense?.insuranceContribution) },
        {
          code: "1g",
          label: "其他支出",
          value: formatNamedCurrency(
            snapshot?.expense?.hasOtherExpense,
            snapshot?.expense?.otherExpenseName,
            snapshot?.expense?.otherExpenseAmount,
          ),
        },
      ],
    },
    {
      key: "cashflowSummary",
      title: "3. 每月可動用收入",
      description: "以收入減支出，保留表單查看頁常見的結餘閱讀節奏。",
      summaryLabel: "估計每月盈餘 / 超支",
      summaryValue: formatCurrencyValue(summaries?.surplusDeficit),
      rows: [
        {
          code: "1h",
          label: "目前每月可動用收入",
          value: formatCurrencyValue(summaries?.surplusDeficit),
          note: "A - B，即收入小計減支出小計",
        },
      ],
    },
    {
      key: "assets",
      title: "4. 資產資料",
      description: "將流動及非流動資產合併成連續查看區塊。",
      summaryLabel: "資產合計",
      summaryValue: summaries
        ? formatCurrencyValue((summaries.totalCurrentAssets ?? 0) + (summaries.totalNonCurrentAssets ?? 0))
        : EMPTY_VALUE,
      rows: [
        { code: "2a", label: "現金及存款", value: formatCurrencyValue(snapshot?.currentAssets?.cashAndDeposits) },
        { code: "2b", label: "高流動性投資", value: formatCurrencyValue(snapshot?.currentAssets?.liquidInvestments) },
        {
          code: "2c",
          label: "其他流動資產",
          value: formatNamedCurrency(
            snapshot?.currentAssets?.hasOtherCurrentAssets,
            snapshot?.currentAssets?.otherCurrentAssetsName,
            snapshot?.currentAssets?.otherCurrentAssetsAmount,
          ),
        },
        { code: "2d", label: "物業", value: formatCurrencyValue(snapshot?.nonCurrentAssets?.property) },
        {
          code: "2e",
          label: "其他非流動資產",
          value: formatCurrencyValue(snapshot?.nonCurrentAssets?.otherNonCurrentAssets),
        },
      ],
    },
    {
      key: "liabilities",
      title: "5. 負債資料",
      description: "保留原資料語義，但改為表單式只讀欄位。",
      summaryLabel: "負債合計",
      summaryValue: formatCurrencyValue(summaries?.totalLiabilities),
      rows: [
        { code: "2f", label: "按揭貸款", value: formatCurrencyValue(snapshot?.liabilities?.mortgageLoan) },
        { code: "2g", label: "私人 / 信用卡貸款", value: formatCurrencyValue(snapshot?.liabilities?.personalCreditCardLoan) },
        {
          code: "2h",
          label: "其他負債",
          value: formatNamedCurrency(
            snapshot?.liabilities?.hasOtherLiabilities,
            snapshot?.liabilities?.otherLiabilityName,
            snapshot?.liabilities?.otherLiabilityAmount,
          ),
        },
      ],
    },
    {
      key: "protection",
      title: "6. 現有保障",
      description: "沿用現行保障欄位，統一放進長表單查看頁。",
      summaryValue: EMPTY_VALUE,
      rows: [
        { code: "3a", label: "人壽保險", value: formatTextValue(snapshot?.protection?.lifeInsurance) },
        { code: "3b", label: "危疾保險", value: formatTextValue(snapshot?.protection?.criticalIllnessInsurance) },
        { code: "3c", label: "醫療保險類別", value: formatTextValue(snapshot?.protection?.medicalInsuranceType) },
        { code: "3d", label: "醫療保險金額", value: formatCurrencyValue(snapshot?.protection?.medicalInsuranceAmount) },
        { code: "3e", label: "意外保險", value: formatTextValue(snapshot?.protection?.accidentInsurance) },
        { code: "3f", label: "其他現有保障", value: formatTextValue(snapshot?.protection?.otherExistingProtection) },
        { code: "3g", label: "附件資料 / 備註", value: formatTextValue(snapshot?.protection?.attachmentRemarks) },
      ],
    },
  ];
}
